/**
 * Valuation Dashboard Forecast Utilities
 * Functions for generating valuation and EBITDA forecast data for dashboard charts
 */

/**
 * Generate valuation forecast data based on current valuation and growth rate
 * @param {Object} params - Parameters for forecast generation
 * @param {Object} params.latestValuation - Latest valuation data
 * @param {number} params.growthRate - Annual growth rate percentage
 * @returns {Array} Forecast data array
 */
export const generateForecastData = ({
  latestValuation,
  growthRate
}) => {
  // Start from the current valuation amount
  const currentValuation = latestValuation?.valuation_amount || 0;
  if (currentValuation === 0) return [];

  const years = 5;
  const data = [];
  const startDate = new Date();

  for (let i = 0; i <= years * 4; i++) { // Quarterly data
    const year = Math.floor(i / 4);
    const month = (i % 4) * 3;
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + month);
    date.setFullYear(date.getFullYear() + year);

    // Project valuation based on growth rate from the CURRENT valuation
    const quarterlyGrowth = (growthRate / 100) / 4; // Convert annual to quarterly
    const projectedValue = currentValuation * Math.pow(1 + quarterlyGrowth, i);

    // Add some variance for the range (±25%)
    const highValue = projectedValue * 1.25;
    const lowValue = projectedValue * 0.75;

    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      value: Math.round(projectedValue),
      high: Math.round(highValue),
      low: Math.round(lowValue)
    });
  }

  return data;
};

/**
 * Generate EBITDA/Gross Profit forecast data based on revenue and EBITDA margin
 * @param {Object} params - Parameters for EBITDA forecast generation
 * @param {Object} params.financials - Financial data object
 * @param {number} params.growthRate - Annual growth rate percentage
 * @param {number} params.ebitdaMargin - Current EBITDA margin percentage
 * @param {Object} params.profitGap - Profit gap data
 * @returns {Array} EBITDA forecast data array
 */
export const generateEbitdaForecastData = ({
  financials,
  growthRate,
  ebitdaMargin,
  profitGap
}) => {
  const currentRevenue = financials.revenue || 0;
  if (currentRevenue === 0) return [];

  const years = 5;
  const data = [];
  const startDate = new Date();

  // Use the slider value for current margin (user adjustable)
  const currentEbitdaMargin = ebitdaMargin;
  // Use industry high from profit gap if available
  const industryHighMargin = profitGap?.industry_high_ebitda_margin || 0;

  for (let i = 0; i <= years * 4; i++) { // Quarterly data
    const year = Math.floor(i / 4);
    const month = (i % 4) * 3;
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + month);
    date.setFullYear(date.getFullYear() + year);

    const quarterlyGrowth = (growthRate / 100) / 4; // Convert annual to quarterly
    const projectedRevenue = currentRevenue * Math.pow(1 + quarterlyGrowth, i);

    // Calculate EBITDA based on slider margin (adjustable)
    const projectedCurrentEbitda = projectedRevenue * (currentEbitdaMargin / 100);

    // Calculate target EBITDA at industry high margin
    const projectedTargetEbitda = industryHighMargin > 0
      ? projectedRevenue * (industryHighMargin / 100)
      : 0;

    // Calculate the profit gap (difference between target and current)
    const profitGapAmount = Math.max(0, projectedTargetEbitda - projectedCurrentEbitda);

    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      revenue: Math.round(projectedRevenue),
      // Stacked EBITDA components
      currentEbitda: Math.round(projectedCurrentEbitda),
      profitGapAmount: Math.round(profitGapAmount),
      // Reference lines
      targetEbitda: industryHighMargin > 0 ? Math.round(projectedTargetEbitda) : null,
      // Legacy for compatibility
      ebitda: Math.round(projectedCurrentEbitda),
      margin: currentEbitdaMargin
    });
  }

  return data;
};
